import { HttpClient } from '@angular/common/http';
import { Component, effect, inject, OnInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter, firstValueFrom, fromEvent, Subscription } from 'rxjs';
import { I18nService } from './core/i18n.service';
import { RouteTitleService } from './core/route-title.service';
import { AiAgentComponent } from './features/ai-agent/ai-agent.component';
import { AuthService } from './features/auth/auth.service';
import { ChatComponent } from './features/chat/chat.component';
import { ChatService } from './features/chat/chat.service';
import { FavouritesService } from './features/favourites/favourites.service';
import { FormsModule } from '@angular/forms';
import { MobilePageHeaderComponent } from './shared/mobile-page-header/mobile-page-header.component';
import { HomeMobileMapShellStateService } from './features/home/home-mobile-map-shell-state.service';
import { LocationSearchComponent } from './features/home/location-search.component';
import { AppDownloadSheetComponent } from './shared/app-download-sheet/app-download-sheet.component';

const ACTIVE_FILTER_QUERY_KEYS = [
  'type',
  'sub_type',
  'deal_type',
  'rent_type',
  'city_name',
  'min_price',
  'max_price',
  'min_square',
  'max_square',
  'min_bedrooms',
  'max_bedrooms',
  'min_bathrooms',
  'max_bathrooms',
  'min_floors',
  'max_floors',
  'min_floor',
  'max_floor',
  'min_building_floors',
  'max_building_floors',
] as const;

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    AiAgentComponent,
    ChatComponent,
    FormsModule,
    MobilePageHeaderComponent,
    LocationSearchComponent,
    AppDownloadSheetComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly chatService = inject(ChatService);
  private readonly favouritesService = inject(FavouritesService);
  private readonly http = inject(HttpClient);
  private readonly mobileMapShellState = inject(HomeMobileMapShellStateService);
  protected readonly i18n = inject(I18nService);
  private readonly routeTitleService = inject(RouteTitleService);
  private resizeSubscription?: Subscription;

  menuOpen = false;
  showChrome = true;
  showSearch = true;
  pageTitle: string | null = null;
  isAuthShell = false;
  isMobile = false;
  showMobileHeaderActions = false;
  showMobileHeaderBack = false;
  showMobileFooter = false;
  showMobileMapShell = false;
  showFooter = true;
  showDesktopChatOverlay = false;
  showDesktopAgentOverlay = false;
  isAdPaymentRoute = false;
  currentPath = '/';
  feedbackOpen = false;
  isFeedbackSending = false;
  feedbackError = '';
  feedbackSuccess = '';
  hasActiveFilters = false;
  feedbackForm = {
    name: '',
    phone: '',
    message: '',
  };
  readonly isAuthenticated = this.authService.isAuthenticated;
  readonly chatUnreadCount = this.chatService.unreadCount;

  protected get mobileMapHeaderOpacity(): number {
    return this.mobileMapShellState.headerOpacity();
  }

  protected get mobileMapControlBackground(): string {
    if (!this.showMobileMapShell) {
      return '#f5f5f5';
    }

    const channel = Math.round(255 - 10 * this.mobileMapHeaderOpacity);
    return `rgb(${channel} ${channel} ${channel})`;
  }

  constructor() {
    void this.routeTitleService;
    effect(() => {
      if (this.isAuthenticated()) {
        queueMicrotask(() => void this.refreshChatUnreadCount());
      } else {
        queueMicrotask(() => {
          this.chatService.clearUnreadCount();
          this.favouritesService.resetState();
          this.closeAuthBoundOverlays();
        });
      }
    });
    this.checkViewport();
    this.syncRouteState(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.syncRouteState(event.urlAfterRedirects));
  }

  ngOnInit(): void {
    this.resizeSubscription = fromEvent(window, 'resize').subscribe(() => {
      this.checkViewport();
      this.syncRouteState(this.router.url);
    });
  }

  ngOnDestroy(): void {
    this.resizeSubscription?.unsubscribe();
  }

  private checkViewport(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  toggleMenu(event?: MouseEvent): void {
    event?.stopPropagation();
    this.menuOpen = !this.menuOpen;
  }

  closeMenu(): void {
    this.menuOpen = false;
  }

  openFeedbackModal(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.isAuthenticated()) {
      void this.router.navigate(['/login']);
      return;
    }

    this.closeMenu();
    this.feedbackError = '';
    this.feedbackSuccess = '';
    this.feedbackOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeFeedbackModal(): void {
    this.feedbackOpen = false;
    this.feedbackError = '';
    this.feedbackSuccess = '';
    document.body.style.overflow = '';
  }

  async submitFeedback(): Promise<void> {
    if (this.isFeedbackSending) {
      return;
    }

    const name = this.feedbackForm.name.trim();
    const phone = this.feedbackForm.phone.trim();
    const message = this.feedbackForm.message.trim();

    if (!name || !phone || !message) {
      this.feedbackError = this.i18n.t('shell.feedbackFillAll');
      return;
    }

    this.isFeedbackSending = true;
    this.feedbackError = '';
    this.feedbackSuccess = '';

    try {
      const payload = new URLSearchParams({
        name,
        phone,
        message,
        type: 'support',
      });

      await firstValueFrom(
        this.http.post('/send-feedback', payload.toString(), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          },
        })
      );

      this.feedbackSuccess = this.i18n.t('shell.feedbackSuccess');
      this.feedbackForm = {
        name: '',
        phone: '',
        message: '',
      };
    } catch {
      this.feedbackError = this.i18n.t('shell.feedbackError');
    } finally {
      this.isFeedbackSending = false;
    }
  }

  onLogout(event?: MouseEvent): void {
    event?.preventDefault();
    this.authService.logout();
    this.closeMenu();
    void this.router.navigate(['/']);
  }

  openFilters(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();

    const [path, queryString = ''] = this.router.url.split('?');
    const params = new URLSearchParams(queryString);
    params.set('filters', '1');

    void this.router.navigateByUrl(`${path || '/'}?${params.toString()}`);
  }

  openChat(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.isAuthenticated()) {
      void this.router.navigate(['/login']);
      return;
    }

    if (this.isMobile) {
      void this.router.navigate(['/chat']);
      return;
    }

    void this.updateDesktopOverlayQuery({ chat: '1', agent: null });
  }

  openAgent(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.isAuthenticated()) {
      void this.router.navigate(['/login']);
      return;
    }

    if (this.isMobile) {
      void this.router.navigate(['/agent']);
      return;
    }

    void this.updateDesktopOverlayQuery({ agent: '1', chat: null });
  }

  openGuestLogin(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeMenu();
    void this.router.navigateByUrl('/login');
  }

  onMobileHeaderBack(): void {
    if (
      this.currentPath === '/my-profile/form' ||
      this.currentPath === '/my-profile/currency' ||
      this.currentPath === '/my-profile/balance'
    ) {
      void this.router.navigate(['/my-profile']);
      return;
    }

    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
      return;
    }

    void this.router.navigate(['/']);
  }

  private resolvePageTitle(): string | null {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) route = route.firstChild;
    if ((route.data['shellTitle'] || (this.isMobile && route.data['mobileShellTitle'])) && route.data['titleKey']) {
      return this.i18n.t(route.data['titleKey'] as Parameters<typeof this.i18n.t>[0]);
    }
    return null;
  }

  private resolveMobileHeaderBack(): boolean {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) route = route.firstChild;
    return this.isMobile && Boolean(route.data['mobileShellBack']);
  }

  private syncRouteState(url: string): void {
    const [pathOnlyRaw, queryString = ''] = url.split('?');
    const pathOnly = pathOnlyRaw || '/';
    const queryParams = new URLSearchParams(queryString);
    const showMobileFiltersOverlay = this.isMobile && queryParams.get('filters') === '1';
    this.currentPath = pathOnly;
    this.isAdPaymentRoute = /^\/ad\/\d+\/payment$/.test(pathOnly);
    this.hasActiveFilters = this.hasActiveListingFilters(pathOnly, queryParams);
    this.pageTitle = this.resolvePageTitle();
    this.showMobileHeaderBack = this.resolveMobileHeaderBack();
    const isAuthRoute = pathOnly === '/login' || pathOnly === '/login/code';
    const isMobileLoginRoute = this.isMobile && pathOnly === '/login';
    const isMobileChatRoute = this.isMobile && pathOnly === '/chat';
    const isMobileAgentRoute = this.isMobile && pathOnly === '/agent';
    const isMobileChatThreadRoute = isMobileChatRoute && queryParams.has('thread');
    const isMobileHomeMapRoute =
      this.isMobile && (pathOnly === '/' || pathOnly.startsWith('/category/'));
    const isMobileAdCreateRoute =
      this.isMobile && (pathOnly === '/ad/new' || /^\/ad\/\d+\/edit$/.test(pathOnly));
    const isMobileFullscreenRoute =
      isMobileAdCreateRoute || isMobileChatRoute || isMobileAgentRoute;
    const isMobileSingleAdRoute = this.isMobile && /^\/ad\/\d+$/.test(pathOnly);
    const isMobileHeaderlessRoute =
      this.isMobile &&
      (isMobileSingleAdRoute ||
        pathOnly === '/subscriptions' ||
        pathOnly === '/contacts' ||
        pathOnly === '/about-us' ||
        pathOnly === '/my-profile' ||
        pathOnly === '/payment' ||
        pathOnly === '/terms' ||
        pathOnly === '/privacy' ||
        pathOnly === '/offer');
    const isDesktopChatRoute = !this.isMobile && pathOnly === '/chat';
    const isDesktopAgentRoute = !this.isMobile && pathOnly === '/agent';
    

    if (isAuthRoute) {
      this.showChrome = false;
      this.isAuthShell = !isMobileLoginRoute;
      this.showSearch = false;
      this.showMobileHeaderBack = false;
      this.showFooter = !isMobileLoginRoute;
      this.showMobileFooter = isMobileLoginRoute;
      this.showMobileMapShell = false;
      this.showDesktopChatOverlay = false;
      this.showDesktopAgentOverlay = false;
      this.closeMenu();
      return;
    }

    if (this.isMobile) {
      this.showChrome = !showMobileFiltersOverlay && !isMobileFullscreenRoute && !isMobileHeaderlessRoute;
      this.isAuthShell = false;
      this.showMobileHeaderActions = true;
      this.showMobileFooter =
        !showMobileFiltersOverlay &&
        !isMobileChatThreadRoute &&
        !isMobileAgentRoute &&
        !isMobileSingleAdRoute;
      this.showMobileMapShell = isMobileHomeMapRoute;
    } else {
      this.showChrome = true;
      this.isAuthShell = false;
      this.showMobileHeaderActions = false;
      this.showMobileFooter = false;
      this.showMobileMapShell = false;
    }

    this.showSearch =
      !this.isAuthShell && (pathOnly === '/' || pathOnly.startsWith('/category/'));
    this.showFooter = !isDesktopChatRoute && !isDesktopAgentRoute;
    this.showDesktopChatOverlay =
      !this.isMobile &&
      pathOnly !== '/chat' &&
      queryParams.get('chat') === '1' &&
      this.isAuthenticated();
    this.showDesktopAgentOverlay =
      !this.isMobile &&
      pathOnly !== '/agent' &&
      queryParams.get('agent') === '1' &&
      this.isAuthenticated();
    if (this.feedbackOpen && (isAuthRoute || isMobileFullscreenRoute || isMobileHeaderlessRoute)) {
      this.closeFeedbackModal();
    }
    this.closeMenu();
  }

  isMobileFooterActive(section: 'home' | 'favourites' | 'agent' | 'chat' | 'profile'): boolean {
    switch (section) {
      case 'home':
        return this.currentPath === '/' || this.currentPath.startsWith('/category/');
      case 'favourites':
        return this.currentPath === '/favourites';
      case 'agent':
        return this.currentPath === '/agent';
      case 'chat':
        return this.currentPath === '/chat';
      case 'profile':
        return this.currentPath === '/my-profile' ||
          this.currentPath === '/my-profile/form' ||
          this.currentPath === '/my-profile/currency' ||
          this.currentPath === '/my-profile/balance' ||
          this.isAdPaymentRoute ||
          this.currentPath === '/payments-history';
      default:
        return false;
    }
  }

  formatUnreadCount(count: number): string {
    return count > 99 ? '99+' : String(count);
  }

  private async refreshChatUnreadCount(): Promise<void> {
    try {
      await firstValueFrom(this.chatService.refreshUnreadCount());
    } catch {
      this.chatService.clearUnreadCount();
    }
  }

  private closeAuthBoundOverlays(): void {
    this.showDesktopChatOverlay = false;
    this.showDesktopAgentOverlay = false;

    const [, queryString = ''] = this.router.url.split('?');
    const queryParams = new URLSearchParams(queryString);
    if (queryParams.has('chat') || queryParams.has('agent')) {
      void this.updateDesktopOverlayQuery({ chat: null, agent: null });
    }
  }

  private updateDesktopOverlayQuery(paramsPatch: Record<string, string | null>): Promise<boolean> {
    const [pathOnly, queryString = ''] = this.router.url.split('?');
    const params = new URLSearchParams(queryString);

    Object.entries(paramsPatch).forEach(([key, value]) => {
      if (value === null) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });

    const nextQuery = params.toString();
    return this.router.navigateByUrl(nextQuery ? `${pathOnly}?${nextQuery}` : pathOnly);
  }

  private hasActiveListingFilters(pathOnly: string, queryParams: URLSearchParams): boolean {
    if (pathOnly.startsWith('/category/')) {
      return true;
    }

    return ACTIVE_FILTER_QUERY_KEYS.some((key) => (queryParams.get(key) ?? '').trim().length > 0);
  }
}
